// src/services/onboarding.service.js
import { api } from './api';

class OnboardingService {
    /**
     * Get all onboarding records
     */
    async getAll() {
        try {
            const response = await api.get('/api/onboarding');
            return response.data.data || [];
        } catch (error) {
            // Silently fail to prevent console spam (non-critical)
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Onboarding records fetch failed:', error.message);
            }
            return [];
        }
    }

    /**
     * Get pending onboarding records
     */
    async getPending() {
        try {
            const response = await api.get('/api/onboarding/pending');
            return response.data.data || [];
        } catch (error) {
            // Silently fail to prevent console spam (non-critical)
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Pending onboarding fetch failed:', error.message);
            }
            return [];
        }
    }

    /**
     * Get completed onboarding records
     */
    async getCompleted() {
        try {
            const response = await api.get('/api/onboarding/completed');
            return response.data.data || [];
        } catch (error) {
            // Silently fail to prevent console spam (non-critical)
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Completed onboarding fetch failed:', error.message);
            }
            return [];
        }
    }

    /**
     * Get checklist configuration
     */
    async getChecklistConfig() {
        try {
            const response = await api.get('/api/onboarding/checklist-config');
            return response.data.data || null;
        } catch (error) {
            // Silently fail to prevent console spam (non-critical)
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Checklist config fetch failed:', error.message);
            }
            return null;
        }
    }

    /**
     * Update checklist configuration
     */
    async updateChecklistConfig(config) {
        try {
            const response = await api.put('/api/onboarding/checklist-config', { config });
            return response.data;
        } catch (error) {
            console.error('Error updating checklist config:', error);
            throw error;
        }
    }

    /**
     * Get onboarding by employee ID
     */
    async getByEmployeeId(employeeId) {
        try {
            const response = await api.get(`/api/onboarding/employee/${employeeId}`);
            return response.data.data || null;
        } catch (error) {
            console.error('Error fetching employee onboarding:', error);
            return null;
        }
    }

    /**
     * Create or update onboarding record
     */
    async upsert(onboardingData) {
        try {
            const response = await api.post('/api/onboarding', onboardingData);
            return response.data;
        } catch (error) {
            console.error('Error upserting onboarding:', error);
            throw error;
        }
    }

    /**
     * Update single field
     */
    async updateField(employeeId, fieldName, fieldValue) {
        try {
            const response = await api.put(`/api/onboarding/employee/${employeeId}/field`, {
                fieldName,
                fieldValue
            });
            return response.data;
        } catch (error) {
            console.error('Error updating field:', error);
            throw error;
        }
    }

    /**
     * Update multiple fields
     */
    async updateFields(employeeId, fields) {
        try {
            const response = await api.put(`/api/onboarding/employee/${employeeId}/fields`, {
                fields
            });
            return response.data;
        } catch (error) {
            console.error('Error updating fields:', error);
            throw error;
        }
    }

    /**
     * Review and approve
     */
    async reviewAndApprove(employeeId, approved, comments = null) {
        try {
            const payload = comments ? { approved, comments } : { approved };
            const response = await api.post(`/api/onboarding/employee/${employeeId}/approve`, payload);
            return response.data;
        } catch (error) {
            console.error('Error reviewing onboarding:', error);
            throw error;
        }
    }

    /**
     * Get documents
     */
    async getDocuments(employeeId) {
        try {
            const response = await api.get(`/api/onboarding/employee/${employeeId}/documents`);
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching documents:', error);
            return [];
        }
    }

    /**
     * Upload document
     */
    async uploadDocument(employeeId, documentData) {
        try {
            const response = await api.post(`/api/onboarding/employee/${employeeId}/documents`, documentData);
            return response.data;
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    }

    // Removed localStorage methods - all data from backend only

    // ============================================================
    // TASKBOX ONBOARDING TASKS
    // ============================================================

    /**
     * Get onboarding tasks for TaskBox widget
     */
    async getOnboardingTasks() {
        try {
            const response = await api.get('/api/onboarding/tasks');
            return response.data;
        } catch (error) {
            console.error('Error fetching onboarding tasks:', error);
            throw error;
        }
    }

    /**
     * Get onboarding progress percentage
     */
    async getOnboardingProgress() {
        try {
            const response = await api.get('/api/onboarding/progress');
            return response.data;
        } catch (error) {
            console.error('Error fetching onboarding progress:', error);
            throw error;
        }
    }

    /**
     * Complete onboarding task
     */
    async completeOnboardingTask(taskId) {
        try {
            const response = await api.put(`/api/onboarding/tasks/${taskId}/complete`);
            return response.data;
        } catch (error) {
            console.error('Error completing onboarding task:', error);
            throw error;
        }
    }

    /**
     * Uncomplete onboarding task
     */
    async uncompleteOnboardingTask(taskId) {
        try {
            const response = await api.put(`/api/onboarding/tasks/${taskId}/uncomplete`);
            return response.data;
        } catch (error) {
            console.error('Error uncompleting onboarding task:', error);
            throw error;
        }
    }

    /**
     * Create onboarding task (Admin/HR only)
     */
    async createOnboardingTask(taskData) {
        try {
            const response = await api.post('/api/onboarding/tasks', taskData);
            return response.data;
        } catch (error) {
            console.error('Error creating onboarding task:', error);
            throw error;
        }
    }

    /**
     * Delete onboarding task (Admin/HR only)
     */
    async deleteOnboardingTask(taskId) {
        try {
            const response = await api.delete(`/api/onboarding/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting onboarding task:', error);
            throw error;
        }
    }
}

export default new OnboardingService();
